// frontend/src/pages/manufacturing/v2/ManufacturingDashboardV2.jsx
//
// Live operational snapshot for the V2 manufacturing module. Reads
// GET /api/v2/dashboard/manufacturing/ and renders:
//   - a row of stat cards (active WOs w/ planned+in-progress split, overdue,
//     completed today/week, units produced today/week, scrap today/week,
//     orders in production / ready to ship);
//   - a "Load by operation" table with bar-scaled open-step counts;
//   - a "Recent completions" list (WO#, item, qty, who, relative when).
// Auto-refreshes every 60s (cleared on unmount); manual refresh button; and
// loading + error states. Matches v2 page styling (GenerationModeBadge header).

import React, { useState, useEffect, useCallback } from 'react';
import {
  Container, Paper, Typography, Box, Button, Alert, Grid,
  Table, TableBody, TableCell, TableHead, TableRow,
  LinearProgress, Divider, List, ListItem, ListItemText,
} from '@mui/material';
import {
  Refresh, Assignment, Warning, CheckCircle, Inventory2,
  DeleteSweep, LocalShipping,
} from '@mui/icons-material';
import { v2 } from '../../../services/api';
import { toast } from 'react-toastify';
import usePageTitle from '../../../hooks/usePageTitle';
import GenerationModeBadge from './components/GenerationModeBadge';

const REFRESH_MS = 60000;

const num = (v) => Number(v || 0).toLocaleString();

/** Compact "x ago" / "just now" formatter for ISO timestamps. */
export function relativeTime(iso) {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const secs = Math.round((Date.now() - then) / 1000);
  if (secs < 45) return 'just now';
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  if (days < 7) return `${days}d ago`;
  const weeks = Math.round(days / 7);
  if (weeks < 5) return `${weeks}w ago`;
  return new Date(iso).toLocaleDateString();
}

/** A single stat card. `error` paints the value red (used for overdue > 0). */
function StatCard({ icon, label, value, sub, error, testId }) {
  return (
    <Paper
      variant="outlined"
      sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column', gap: 0.5 }}
      data-testid={testId}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
        {icon}
        <Typography variant="body2" sx={{ fontWeight: 600 }}>{label}</Typography>
      </Box>
      <Typography
        variant="h4"
        sx={{ fontWeight: 700, color: error ? 'error.main' : 'text.primary', lineHeight: 1.1 }}
      >
        {value}
      </Typography>
      {sub != null && (
        <Typography variant="caption" color="text.secondary">{sub}</Typography>
      )}
    </Paper>
  );
}

const ManufacturingDashboardV2 = () => {
  usePageTitle('Manufacturing Dashboard V2');

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    setError(null);
    try {
      const res = await v2.dashboard.manufacturing();
      setData(res.data);
    } catch (err) {
      console.error('Failed to load manufacturing dashboard:', err);
      setError('Failed to load dashboard data');
      if (!silent) toast.error('Failed to load dashboard');
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Auto-refresh every 60s; silent so we don't flash the loading bar.
  useEffect(() => {
    const id = setInterval(() => fetchData({ silent: true }), REFRESH_MS);
    return () => clearInterval(id);
  }, [fetchData]);

  const active = data?.active || {};
  const completed = data?.completed || {};
  const unitsProduced = data?.units_produced || {};
  const scrap = data?.scrap || {};
  const orders = data?.orders || {};
  const operations = data?.operations || [];
  const recent = data?.recent_completions || [];
  const overdue = data?.overdue_count || 0;

  const maxOpenSteps = operations.reduce((m, o) => Math.max(m, o.open_steps || 0), 0);

  return (
    <Container maxWidth={false} sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h4" fontWeight={600} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          Manufacturing Dashboard
          <GenerationModeBadge />
        </Typography>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={() => fetchData()}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      {loading && <LinearProgress sx={{ mb: 2 }} data-testid="loading-state" />}
      {error && <Alert severity="error" sx={{ mb: 2 }} data-testid="error-state">{error}</Alert>}

      {data && (
        <>
          <Grid container spacing={2} sx={{ mb: 3 }} data-testid="stat-cards">
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                icon={<Assignment fontSize="small" />}
                label="Active Work Orders"
                value={num(active.total)}
                sub={`${num(active.planned)} planned · ${num(active.in_progress)} in progress`}
                testId="card-active"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                icon={<Warning fontSize="small" />}
                label="Overdue"
                value={num(overdue)}
                sub={overdue > 0 ? 'past due date' : 'none overdue'}
                error={overdue > 0}
                testId="card-overdue"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                icon={<CheckCircle fontSize="small" />}
                label="Completed"
                value={num(completed.today)}
                sub={`today · ${num(completed.week)} this week`}
                testId="card-completed"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                icon={<Inventory2 fontSize="small" />}
                label="Units Produced"
                value={num(unitsProduced.today)}
                sub={`today · ${num(unitsProduced.week)} this week`}
                testId="card-units"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                icon={<DeleteSweep fontSize="small" />}
                label="Scrap"
                value={num(scrap.today)}
                sub={`today · ${num(scrap.week)} this week`}
                testId="card-scrap"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                icon={<LocalShipping fontSize="small" />}
                label="Orders In Production"
                value={num(orders.in_production)}
                sub={`${num(orders.ready_to_ship)} ready to ship`}
                testId="card-orders"
              />
            </Grid>
          </Grid>

          <Grid container spacing={3}>
            <Grid item xs={12} md={7}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="h6" sx={{ mb: 1.5 }}>Load by operation</Typography>
                {operations.length === 0 ? (
                  <Typography color="text.secondary" sx={{ py: 2 }} data-testid="operations-empty">
                    No open steps.
                  </Typography>
                ) : (
                  <Table size="small" data-testid="operations-table">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold' }}>Operation</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', width: '45%' }}>Open steps</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>Open qty</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {operations.map((op) => {
                        const pct = maxOpenSteps > 0
                          ? Math.round(((op.open_steps || 0) / maxOpenSteps) * 100)
                          : 0;
                        return (
                          <TableRow key={op.operation_code} data-testid={`op-row-${op.operation_code}`}>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {op.operation_name || op.operation_code}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {op.operation_code}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box sx={{ flex: 1 }}>
                                  <LinearProgress
                                    variant="determinate"
                                    value={pct}
                                    sx={{ height: 8, borderRadius: 4 }}
                                  />
                                </Box>
                                <Box sx={{ minWidth: 32, textAlign: 'right', fontWeight: 600 }}>
                                  {num(op.open_steps)}
                                </Box>
                              </Box>
                            </TableCell>
                            <TableCell align="right">{num(op.open_qty)}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
              </Paper>
            </Grid>

            <Grid item xs={12} md={5}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="h6" sx={{ mb: 1.5 }}>Recent completions</Typography>
                {recent.length === 0 ? (
                  <Typography color="text.secondary" sx={{ py: 2 }} data-testid="recent-empty">
                    No recent completions.
                  </Typography>
                ) : (
                  <List dense disablePadding data-testid="recent-list">
                    {recent.map((r, idx) => (
                      <React.Fragment key={`${r.wo_number}-${idx}`}>
                        {idx > 0 && <Divider component="li" />}
                        <ListItem disableGutters data-testid={`recent-row-${idx}`}>
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  {r.wo_number} · {num(r.quantity)} units
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                                  {relativeTime(r.completed_at)}
                                </Typography>
                              </Box>
                            }
                            secondary={
                              <Typography variant="caption" color="text.secondary" component="span">
                                {r.item_name || r.item_code}
                                {r.completed_by ? ` · ${r.completed_by}` : ''}
                              </Typography>
                            }
                          />
                        </ListItem>
                      </React.Fragment>
                    ))}
                  </List>
                )}
              </Paper>
            </Grid>
          </Grid>
        </>
      )}
    </Container>
  );
};

export default ManufacturingDashboardV2;
